import { useEffect, useMemo, useRef } from 'react';
import {
  ActivityIndicator,
  StyleSheet,
  useWindowDimensions,
  View,
} from 'react-native';
import { useRoute } from '@react-navigation/native';
import { SPACING } from 'constants/design-system';

import { ItemError } from 'modules/ChefScene/components/Item';
import { RouteNames } from 'navigation/linkingConfig';
import { useAppSelector } from 'store/hooks';
import {
  NotificationActions,
  NotificationsSelectors,
} from 'store/Notifications';
import { NotificationTypes } from 'store/Notifications/enums';
import { OrderActions, OrderSelectors } from 'store/Orders';
import { selectIsFetching } from 'store/util/functions';
import { useEnterScreen } from 'util/analytics';
import { withErrorBoundary } from 'components';
import { OrderDetailCardForStateContainer } from 'components/OrderDetailCard';
import { OrderDetailCardMenuControlForStateContainer } from 'components/OrderDetailCard/OrderDetailCardMenuControl';
import { OrderDetailCardStateContainer } from 'components/OrderDetailCard/OrderDetailCardStateContainer';

import { Header } from './components/Header';
import type { Order, OrderItem } from 'store/Orders/types';

type Props = {
  canGoBack?: boolean;
  onPressGoBack: () => void;
  item?: OrderItem;
  order?: Order;
  isRemoved?: boolean;
};

function ItemDetailViewComponent({
  canGoBack,
  item,
  order,
  onPressGoBack,
  isRemoved,
}: Props) {
  const { height } = useWindowDimensions();

  if (!order) {
    return null;
  }

  return (
    <View
      style={{
        maxHeight: height,
        flexGrow: 1,
        gap: SPACING.theme[6],
        padding: 24,
        paddingHorizontal: 96,
      }}
    >
      <OrderDetailCardStateContainer orderUuid={order.uuid}>
        {(state) => (
          <>
            <View style={{ paddingHorizontal: SPACING.theme[12] }}>
              <OrderDetailCardMenuControlForStateContainer
                {...state}
                isViewEntireOrderButtonHidden={true}
                selectedOrderItemUuid={item?.uuid}
                order={order}
                onPressClose={onPressGoBack}
              />
            </View>
            <OrderDetailCardForStateContainer
              {...state}
              selectedOrderItemUuid={item?.uuid}
              order={order}
              isCancelledOverride={isRemoved}
              items={item ? [item] : []}
              itemOverride={item}
              style={{ flexShrink: 1, minHeight: '50%' }}
            />
          </>
        )}
      </OrderDetailCardStateContainer>
    </View>
  );
}

const ItemDetailError = (props: Props) => {
  const { onPressGoBack } = props;

  return (
    <View style={styles.container}>
      <Header style={{ backgroundColor: 'white' }} onClose={onPressGoBack} />
      <ItemError {...props} />
    </View>
  );
};

const WithErrorBoundary = withErrorBoundary(ItemDetailViewComponent, {
  screen: RouteNames.Home.Main.ItemDetails,
  key: 'itemDetails-item',
  renderFallback: (props: any) => <ItemDetailError {...props} />,
});

export function ItemDetailView({ navigation }: { navigation: any }) {
  useEnterScreen('itemDetails');

  const { uuid } = (useRoute().params || {}) as {
    uuid?: string;
  };

  const notifications = useAppSelector(NotificationsSelectors.selectAll);
  const storedItem: OrderItem | undefined = useAppSelector((state) =>
    uuid ? OrderSelectors.selectItemById(state, uuid) : undefined,
  );

  const isFetchingNotification = useAppSelector((state) =>
    selectIsFetching(state, NotificationActions.fetch),
  );
  const isFetchingOrder = useAppSelector((state) =>
    selectIsFetching(state, OrderActions.fetch),
  );
  const isFetched = useRef(false);
  const isFetching =
    !isFetched.current && (isFetchingNotification || isFetchingOrder);

  const isRemoved = Boolean(!isFetching && uuid && !storedItem);

  useEffect(() => {
    if (isFetching || isFetched.current) return;

    isFetched.current = true;
  }, [isFetching]);

  const { item, orderUUID } = useMemo(() => {
    if (storedItem) {
      return {
        item: storedItem,
        orderUUID: storedItem.order_uuid,
      };
    }

    const notification = notifications.find(
      (notification) =>
        notification.notification_type === NotificationTypes.ITEM_REMOVED &&
        notification.item_uuid === uuid,
    );

    if (notification) {
      return {
        item: (notification.meta as OrderItem) ?? undefined,
        orderUUID: notification.order_uuid,
      };
    }

    return {};
  }, [notifications, storedItem, uuid]);

  const order: Order | undefined = useAppSelector((state) =>
    orderUUID ? OrderSelectors.selectById(state, orderUUID) : undefined,
  );

  const onPressGoBack = () => {
    if (navigation.canGoBack()) {
      navigation.goBack();
    } else {
      navigation.replace(RouteNames.Home.Main._);
    }
  };

  useEffect(() => {
    if (!order || !item) return;

    navigation.setOptions({
      title: `${item.quantity} - ${item.name} | #${order.description}`,
    });
  }, [navigation, item, order]);

  if (isFetching) {
    return (
      <View
        style={{
          flex: 1,
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <ActivityIndicator color="white" size={50} />
      </View>
    );
  }

  return (
    <WithErrorBoundary
      item={item}
      order={order}
      canGoBack={navigation.canGoBack()}
      onPressGoBack={onPressGoBack}
      isRemoved={isRemoved}
    />
  );
}

const styles = StyleSheet.create({
  container: {
    backgroundColor: 'white',
    borderRadius: 8,
    flexGrow: 1,
    maxHeight: '100%',
  },
  overlay: {
    bottom: 0,
    left: 0,
    position: 'absolute',
    right: 0,
    top: 0,
  },
});
